/* eslint-disable no-param-reassign */
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { DataField } from "@iventis/domain-model/model/dataField";
import { LocalGeoJson, LocalGeoJsonObject } from "@iventis/map-types";
import { MapModuleLayer } from "../types/store-schema";
import { getAllMapObjectRepeatedTimeRangeValues, isMapObjectWithinSingleRepeatedTimeRange } from "./repeated-time-ranges-map-helpers";
import { DomainLayer } from "../state/map.state.types";

/**
 * Sets a dateFiltered property on the map objects based on the repeated time ranges data fields
 */
export function setLocalMapObjectsDateFilterValue(
    localGeojson: LocalGeoJson,
    layers: Pick<MapModuleLayer, "remote" | "dataFields" | "id">[],
    dayFilter: number,
    timeFilter: number,
    mapObjectIdsNotToFilter: string[]
) {
    const filteredMapObjectIds: string[] = [];
    layers.forEach((layer) => {
        // Ensure we are only checking user layers and not system ones such as analysis or area select
        if (layer.remote) {
            // Only check the layers that have repeated time ranges data fields
            const repeatedTimeRangesDataFields = layer.dataFields.filter((df) => df.type === DataFieldType.RepeatedTimeRanges);
            if (repeatedTimeRangesDataFields.length > 0) {
                const mapObjects = localGeojson[layer.id];
                if (mapObjects != null) {
                    const filteredMapObjectIdsForLayer = setLocalMapObjectsDateFilterValueForLayer(
                        repeatedTimeRangesDataFields,
                        mapObjects,
                        dayFilter,
                        timeFilter,
                        mapObjectIdsNotToFilter
                    );
                    filteredMapObjectIds.push(...filteredMapObjectIdsForLayer);
                }
            }
        }
    });
    return filteredMapObjectIds;
}

/*
 * Sets a dateFiltered property on the map objects based on the repeated time ranges data fields for a given layer
 */
function setLocalMapObjectsDateFilterValueForLayer(
    repeatedTimeRangesDataFields: DataField[],
    mapObjects: LocalGeoJsonObject[],
    dayFilter: number,
    timeFilter: number,
    mapObjectIdsNotToFilter: string[]
) {
    const filteredMapObjectIds: string[] = [];
    mapObjects?.forEach(({ feature }) => {
        if (!mapObjectIdsNotToFilter.includes(feature.properties.id)) {
            // Get all of the repeated time range values for the map object
            const dateFilterValues = getAllMapObjectRepeatedTimeRangeValues(feature, repeatedTimeRangesDataFields);
            const isFiltered = dateFilterValues.length === 0 ? false : !isMapObjectWithinSingleRepeatedTimeRange(dateFilterValues, dayFilter, timeFilter);
            // Set the dateFiltered property
            feature.properties.dateFiltered = isFiltered;
            if (isFiltered) {
                filteredMapObjectIds.push(feature.properties.id);
            }
        }
    });

    return filteredMapObjectIds;
}

/** Updates the dateFiltered property on the map objects once datafield is deleted */
export function updateDateFilteredPropertyWhenDataFieldDeleted(
    layer: DomainLayer,
    localMapObjects: LocalGeoJsonObject[],
    deletedDataFieldId: string,
    filter: { day?: number; time?: number }
) {
    const nonDeletedRepeatedTimeRangeDataFields = layer.dataFields.filter(
        (dataField) => dataField.type === DataFieldType.RepeatedTimeRanges && dataField.id !== deletedDataFieldId
    );
    setLocalMapObjectsDateFilterValueForLayer(nonDeletedRepeatedTimeRangeDataFields, localMapObjects, filter.day, filter.time, []);
}
